<template>
  <v-card class="h-100" elevation="0" rounded="lg">
    <div v-if="widgetData.loading" class="px-6">
      <v-skeleton-loader type="heading" class="mb-2 bg-transparent" />
      <v-skeleton-loader type="divider" class="mb-2 bg-transparent" />
      <v-skeleton-loader type="heading" class="mb-2 bg-transparent" />
      <v-skeleton-loader type="heading" class="mb-2 bg-transparent" />
      <v-skeleton-loader type="heading" class="mb-2 bg-transparent" />
      <v-skeleton-loader type="heading" class="mb-2 bg-transparent" />
      <v-skeleton-loader type="heading" class="mb-2 bg-transparent" />
    </div>

    <template v-else-if="widgetData.data">
      <v-card-title class="d-flex align-center px-6 py-3">
        <span class="subtitle1">{{ $t(`coroImpact.widgets.${widgetKey}.title`) }}</span>
        <span class="body2 ml-2">({{ $t(`coroImpact.widgets.${widgetKey}.subtitle`) }})</span>
      </v-card-title>
      <v-divider class="border-opacity-100" color="indigo-faint" />
      <v-card-text class="pa-6 h-100">
        <div
          v-if="!widgetData.data.length"
          class="d-flex flex-column align-center justify-center h-100 pb-6 empty-state-wrapper"
          data-testid="top-delta-widget-no-data"
        >
          <v-icon
            class="mb-1"
            icon="$dataNoHexagon"
            size="80"
            data-testid="top-delta-widget-no-data-icon"
          ></v-icon>
          <div class="headline6 mb-1">
            {{ $t("general.noDataAvailable") }}
          </div>
        </div>
        <template v-else>
          <p class="body2 mb-4">{{ $t(`coroImpact.widgets.${widgetKey}.description`) }}</p>
          <v-list class="pa-0" lines="two" bg-color="transparent">
            <v-list-item
              v-for="(user, index) in widgetData.data"
              :key="index"
              class="px-4 py-3 mb-2"
              :style="getListItemStyle(index)"
              rounded="lg"
              @click="onUserClick(user)"
            >
              <div class="d-flex align-center">
                <v-icon class="mr-4" size="40" icon="$user"></v-icon>
                <div class="d-flex flex-column">
                  <v-list-item-title class="subtitle2">{{ user.displayName }}</v-list-item-title>
                  <v-list-item-subtitle class="body3">
                    <span>{{ user.email }}</span>
                    <span v-if="context === 'msp' && user.workspaceName">
                      ({{ user.workspaceName }})
                    </span>
                  </v-list-item-subtitle>
                </div>
              </div>

              <template v-slot:append>
                <div class="incident-count text-primary">
                  {{ user.count }}
                </div>
              </template>
            </v-list-item>
          </v-list>
        </template>
      </v-card-text>
    </template>

    <div
      v-else
      class="h-100 d-flex align-center justify-center flex-column pa-6"
      data-testid="widget-error-state"
    >
      <v-icon icon="$alertTriangle" size="80"></v-icon>
      <span class="subtitle1 mb-4"> {{ $t(`coroImpact.widgets.${widgetKey}.error`) }}</span>
      <div
        class="coro-link"
        data-testid="retry-fetch-data-button"
        @click="$emit('retry-fetch-data')"
      >
        {{ $t("general.retry") }}
      </div>
    </div>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { useRouter } from "vue-router";
import { RouteName } from "@/constants/routes.ts";
import { FilterContext, useFiltersStore } from "@/_store/filters.module.ts";
import type { DataValue, TimeFrame } from "@/types.ts";
import type { CoroImpactSpotlightUser } from "@/_store/coro-impact/coro-impact.module.ts";
import { TicketsModuleFilter, TicketStatus } from "@/constants/tickets.ts";
import { SearchSuggestion } from "@/constants/general.ts";
import { trackGtmEvent } from "@/_helpers/analytics";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import startCase from "lodash/startCase";

export default defineComponent({
  props: {
    widgetKey: {
      type: String as PropType<"topVulnerableUsersWidget" | "topViolatorUsersWidget">,
      required: true,
    },
    widgetData: {
      type: Object as PropType<DataValue<CoroImpactSpotlightUser[]>>,
      required: true,
    },
    color: {
      type: String as PropType<"purple-faint" | "orange-faint">,
      required: true,
    },
    context: {
      type: String as PropType<"single-view" | "msp">,
      default: "single-view",
    },
    filterTimeFrame: {
      type: Object as PropType<TimeFrame>,
      required: true,
    },
  },
  emits: ["retry-fetch-data"],
  setup(props) {
    const router = useRouter();
    const filtersStore = useFiltersStore();

    const onUserClick = (user: CoroImpactSpotlightUser) => {
      trackGtmEvent(
        GtmAction.CORO_INSIGHTS_PAGE,
        `${startCase(props.context)} - ${startCase(props.widgetKey)} - ${GTMParams.USER_CLICKED}`
      );

      const route = props.context === "msp" ? RouteName.MSP_TICKETS_PAGE : RouteName.TICKETS;
      const context = props.context === "msp" ? FilterContext.MSP_TICKETS : FilterContext.TICKETS;
      const widget =
        props.widgetKey === "topViolatorUsersWidget"
          ? TicketsModuleFilter.USER_DATA_GOVERNANCE
          : null;
      const search =
        props.widgetKey === "topViolatorUsersWidget"
          ? `${SearchSuggestion.VIOLATOR}${user.email}`
          : user.email;
      filtersStore.setFilters(context, {
        widget,
        search,
        eventTimeRange: {
          ...props.filterTimeFrame,
        },
        status: TicketStatus.ALL,
      });
      return router.push({ name: route });
    };

    /**
     * Generates the style for a list item based on its index using custom opacity steps.
     * @param {number} index - The index of the item in the list.
     * @returns {object} A style object with a calculated backgroundColor.
     */
    const getListItemStyle = (index: number) => {
      const opacitySteps = [1, 0.8, 0.6, 0.45, 0.35];

      // Use the opacity step for the current index. If the index is
      // greater than the number of steps, use the last step's opacity.
      const opacity = opacitySteps[index] ?? opacitySteps[opacitySteps.length - 1];

      return {
        // Uses the Vuetify CSS variable for the specified theme color
        // and applies the calculated opacity.
        backgroundColor: `rgba(var(--v-theme-${props.color}), ${opacity})`,
      };
    };

    return {
      onUserClick,
      getListItemStyle,
    };
  },
});
</script>

<style scoped lang="scss">
.incident-count {
  font-family: Quicksand, sans-serif;
  font-size: 28px;
  font-style: normal;
  font-weight: 600;
  line-height: 105%; /* 29.4px */
  letter-spacing: -0.3px;
}

:deep(.empty-state-wrapper) {
  .icon-data-no-hex-l:before {
    color: rgb(var(--v-theme-indigo-light)) !important;
  }
}
</style>
